<script lang="ts">
  import { TargetMarker, CurrentMarker } from "../Program/ProgramElements/SetupFormInputs/SVGMarkers";
  import { maybeFade, uniqueId } from "@/Util/utils";
  import * as Popover from "$lib/components/ui/popover";


  export let trainingAdvice;
  export let isCalendar = false;
  export let showDate = false;
  export let date = new Date();
  export let enableFade = true;
  export let showTooltip = true;
  export let showColour = true;
  export let hideLeftLabel = false;
  export let expected_tsRating: number = null;
  export let expected_ts_color: string = '#6a7e9d';
  export let showText = true

  const idKey = uniqueId('ts-')
  
  let title = "";
  const defaultColor = '#374151'
  let starColor = defaultColor
  let starRating = 0;
  $: if (trainingAdvice && trainingAdvice.training_status) {
    const starData = trainingAdvice.training_status;
    if (isCalendar && showText) {
      title = "Training Load: " + starData.tl_total.toFixed(1) + "\nRecovery Load: " + starData.rl_total.toFixed(1);
    } else if (showText) {
      title = "Training Status: " + starData.cat + "\nForm: " + starData.form_cat;
    }
    if (showColour) starColor = starData.icon_color;
    starRating = 5 * starData.ts_rating;
  }

  const fmtLoad = (v: number, digits: number) => (typeof v === 'number' ? v.toFixed(digits) : '-');
  $: status = trainingAdvice?.training_status;
  $: hasLoads = status && typeof status.tl_low === 'number';

  const starNumbers = [0,1,2,3,4]

  const getStarFill = (starWidth, showColour, expectedStarWidth, starNumber) => {
    if (starWidth <= 0) {
      if (expectedStarWidth <= 0) return '#d6d6d6' // solid gray
      else if (expectedStarWidth >= 100) return expected_ts_color // solid expected tsr color
      else return `url(#expected_ts_grad_${idKey}_${starNumber})` //partial fill using expected tsr (expected-gray)
    }
    else if (starWidth >= 100) return starColor // solid star color
    else if (showColour) return `url(#ts_star_gradient_color_${idKey}_${starNumber})` //partial fill using star color (primary-expected-gray)
    else return `url(#ts_star_gradient_noColor_${idKey}_${starNumber})` //partial fill using default color (primary-expected-gray)
  }
</script>

{#if !hideLeftLabel}
    <span class="title">
        <!-- Populated by ::after -->
    </span>
{/if}
<span class="whitespace-nowrap">
    {#if starRating != null}
    <div class:noMargin={hideLeftLabel} class="star-rating h-full" {title}
    in:maybeFade={{duration: 1000, enableFade}} >
      <div class="back-stars h-full">
        {#each starNumbers as starNumber}
          {@const starWidth = (starRating - starNumber) * 100}
          {@const expectedRating = (expected_tsRating ?? 0) * 5}
          {@const expectedStarWidth = ((expectedRating) - starNumber) * 100}
          {@const useExpectedMarker = expectedStarWidth > 0 && expectedStarWidth <= 100}
          {@const isPartialStarWidth = starWidth > 0 && starWidth < 100}
          {@const isAhead = starRating >= expectedRating}
          <div class="h-full relative">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
              {#if isPartialStarWidth || useExpectedMarker}
                {@const usableExpectedStarWidth = expectedStarWidth >= 100 ? 100 : expectedStarWidth > 0 ? expectedStarWidth : starWidth}
                <defs>
                  <linearGradient id="ts_star_gradient_color_{idKey}_{starNumber}" x1="0%" x2="100%" y1="0%" y2="0%">
                    <stop offset={`${starWidth}%`} stop-color={starColor} /> <!-- yellow -->
                    <stop offset={`${starWidth}%`} stop-color={expected_ts_color} /> <!-- expected tsr -->
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color={expected_ts_color} /> <!-- expected tsr -->
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color="#d6d6d6" /> <!-- gray -->
                  </linearGradient>
                  <linearGradient id="ts_star_gradient_noColor_{idKey}_{starNumber}" x1="0%" x2="100%" y1="0%" y2="0%">
                    <stop offset={`${starWidth}%`} stop-color={defaultColor} /> <!-- yellow -->
                    <stop offset={`${starWidth}%`} stop-color={expected_ts_color} /> <!-- expected tsr -->
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color={expected_ts_color} /> <!-- expected tsr -->
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color="#d6d6d6" /> <!-- gray -->
                  </linearGradient>
                  <linearGradient id="expected_ts_grad_{idKey}_{starNumber}" x1="0%" x2="100%" y1="0%" y2="0%">
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color={expected_ts_color} /> <!-- expected tsr -->
                    <stop offset={`${usableExpectedStarWidth}%`} stop-color="#d6d6d6" /> <!-- gray -->
                  </linearGradient>
                </defs>
              {/if}
              <path fill={getStarFill(starWidth, showColour, expectedStarWidth, starNumber)} d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
            </svg>

            {#if useExpectedMarker}
              <TargetMarker class="text-blue-400 absolute top-[-8px] h-[15px]" style="transform: translate(-50%, -50%); left: {expectedStarWidth}%" strokeWidth={10} />
            {/if}
            {#if isPartialStarWidth && expected_tsRating}
              <CurrentMarker class="text-blue-400 absolute top-[-8px] h-[9px]" style="transform: translate(-50%, -50%); left: {starWidth}%" />
            {/if}
          </div>
          {/each}
      </div>
    </div>
    {/if}
    {#if showTooltip}
    <Popover.Root portal="#shadcn-ui-dialog-target">
      <Popover.Trigger class="tooltip-icon rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring inline-flex items-center justify-center w-6 h-6 -my-1 bg-transparent border-0 p-0 cursor-pointer" aria-label="Training Status help">
        <i class="fa-regular fa-circle-question"></i>
      </Popover.Trigger>
      <Popover.Content side="top" class="w-[300px] max-w-[calc(100vw-2rem)] min-w-0 p-3 text-gray-600 text-sm">
  <div class="xert-svelte">
    <table>
      <tbody>
        <tr>
          <td>
            <div>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <span> Pro Level</span>
            </div>
            <div>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <span> Elite</span>
            </div>
            <div>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <span> Competitive</span>
            </div>
            <div>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa-star"></i>
              <span> Trained</span>
            </div>
            <div>
              <i class="fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <span> Recreational</span>
            </div>
            <div>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <i class="text-[#d6d6d6] fa fa fa-star"></i>
              <span> Untrained</span>
            </div>
          </td>
          
          <td style="padding-left: 6px;">
            <div>
              <i class="fa fa fa-star icon-brown"></i>
              <span> Detraining </span>
            </div>
            <div>
              <i class="fa fa fa-star icon-green"></i>
              <span> Very fresh </span>
            </div>
            <div>
              <i class="fa fa fa-star icon-blue"></i>
              <span> Fresh </span>
            </div>
            <div>
              <i class="fa fa fa-star icon-yellow"></i>
              <span> Tired </span>
            </div>
            <div>
              <i class="fa fa fa-star icon-red"></i>
              <span> Very tired </span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    {#if hasLoads}
      <div class="mt-2 pt-2 border-0 border-t border-solid border-gray-200">
        <div class="font-semibold mb-1">Training Load</div>
        <div class="grid grid-cols-2 gap-x-4">
          <span>Total: <strong>{fmtLoad(status.tl_total, 0)}</strong></span>
          <span>Low: <strong>{fmtLoad(status.tl_low, 0)}</strong></span>
          <span>High: <strong>{fmtLoad(status.tl_high, 1)}</strong></span>
          <span>Peak: <strong>{fmtLoad(status.tl_peak, 1)}</strong></span>
        </div>
      </div>
    {/if}
    {#if showDate}
      <br>
      <span>Training Advice as of {date.toDateString()}</span>
    {/if}
    <div class="mt-2">
      <a class="text-[#2f7076] underline" href="https://baronbiosys.com/glossary/training-status-and-form/" target="_help">Learn more about Training Status</a>
    </div>
  </div>
      </Popover.Content>
    </Popover.Root>
    {/if}
</span>

<style lang="scss">

  .title {
    &::after {
      content: "Training Status:";
    }
    @media screen and (max-width: 1200) {
      &::after {
        content: "Status:";
      }
    }
  }

  /*---------- star rating styles FROM my_fitness.css ----------*/
  .star-rating, .back-stars {
      display: flex;
  }

  .star-rating {
    align-items: left;
    justify-content: left;
    margin-top: 5px;
    margin-left: 4px;

    display: inline-block;
    &.noMargin {
      margin: 0;
    }
  }

  .back-stars {
    position: relative;
    gap: 4px;
    justify-content: space-between;
    > div {
      width: 18px;
    }
  }
</style>
